import React, { useMemo, useState } from "react";
import { Checkbox, FormControl, FormLabel, InputAdornment, ListItemText, MenuItem, Select, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

/*
 * Compact multi-select for long option lists (cities, districts, vehicle
 * types). Selected values show as "İstanbul, Ankara, Bursa +2"; the menu has
 * a search box, "Tümünü Seç" and "Seçimleri Temizle". Nothing is selected by
 * default unless the caller passes a value.
 *
 *   options: [{ value, label }]   value: array of option values
 *   onChange(nextValues)          emptyLabel: text when nothing is selected
 */
export default function MultiSelectDropdown({ label, options, value = [], onChange, placeholder = "Seçin…", emptyLabel = "Seçilmedi", disabled, minWidth = 260, size = "small", maxVisible = 3, searchable = true, sx }) {
  const [query, setQuery] = useState("");
  const selected = Array.isArray(value) ? value : [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("tr");
    return needle ? options.filter((option) => String(option.label).toLocaleLowerCase("tr").includes(needle)) : options;
  }, [options, query]);

  const renderValue = (current) => {
    if (!current.length) return <span style={{ color: "#94A3B8" }}>{emptyLabel}</span>;
    const labels = current.map((item) => options.find((option) => option.value === item)?.label || item);
    return labels.slice(0, maxVisible).join(", ") + (labels.length > maxVisible ? ` +${labels.length - maxVisible}` : "");
  };

  const toggleAll = () => {
    const visibleValues = filtered.map((option) => option.value);
    const allSelected = visibleValues.every((item) => selected.includes(item));
    onChange(allSelected ? selected.filter((item) => !visibleValues.includes(item)) : [...new Set([...selected, ...visibleValues])]);
  };

  return (
    <FormControl size={size} style={{ minWidth }} disabled={disabled} sx={sx}>
      {label ? <FormLabel sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", marginBottom: "4px" }}>{label}</FormLabel> : null}
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={(event) => onChange(typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value)}
        renderValue={renderValue}
        onClose={() => setQuery("")}
        MenuProps={{ PaperProps: { style: { maxHeight: 380, minWidth: 280 } }, autoFocus: false }}
        sx={{ backgroundColor: "#FFFFFF", borderRadius: "10px", fontSize: 13.5 }}
      >
        {searchable ? (
          <div style={{ padding: "6px 12px 8px", position: "sticky", top: 0, backgroundColor: "#FFFFFF", zIndex: 1 }} onKeyDown={(event) => event.stopPropagation()}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={placeholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} /></InputAdornment> }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <Typography onClick={toggleAll} style={{ fontSize: 12.5, fontWeight: 700, color: "#4F46E5", cursor: "pointer" }}>Tümünü Seç</Typography>
              <Typography onClick={() => onChange([])} style={{ fontSize: 12.5, fontWeight: 700, color: "#64748B", cursor: "pointer" }}>Seçimleri Temizle</Typography>
            </div>
          </div>
        ) : null}
        {filtered.map((option) => (
          <MenuItem key={option.value} value={option.value} dense sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <Checkbox size="small" checked={selected.includes(option.value)} />
            <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 13.5 }} />
          </MenuItem>
        ))}
        {!filtered.length ? <MenuItem disabled><ListItemText primary="Sonuç yok" primaryTypographyProps={{ fontSize: 13 }} /></MenuItem> : null}
      </Select>
    </FormControl>
  );
}
